import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { screen } from "@stoneage/XDL/Helpers";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    logoImage = "logoImage",
    isHeaderLogo = "isHeaderLogo",
    logoImageSticky = "logoImageSticky",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.logoImageSticky]: string;
        [fields.isHeaderLogo]: boolean;
        [fields.customCSS]: string;
    };
    extendedStyles?: LogoStyles;
    externalCustomCSS?: string;
}

export interface LogoStyles {
    wrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    menu?: InjectableCss;
    menuTriggerButton?: ClickablePresentationProps;
    image?: LazyImageProps;
    logoimage?: LazyImageProps;
    linkStyle?: LinkPresentationProps;
    logoLinkStyle?: LinkPresentationProps;
    menuIcon?: IconPresentationProps;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            max-width: 100%;
            max-height: 100%;
            display: flex;
            img {
                width: 100%;
                height: 100%;
            }
        `,
    },
    logoimage: {},
    menu: {
        css: css`
            display: flex;
            align-items: center;
            padding-right: 15.5px;
            ${({ sticky }: any) => !sticky && `display: none;`}
            ${screen("lte", "md")`display: flex;padding-right: 20px;
            `}
            @media screen and (max-width: 359px) {
                padding-right: 10px;
            }
        `,
    },
    menuTriggerButton: {},
    image: {
        css: css`
            width: 100%;
        `,
    },
    linkStyle: {},
    linkWrapper: {
        css: css``,
    },
};

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;
    return {
        isSticky: state.xHeader.isSticky,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        homePageLink: getHomePageUrl(state),
        isHomePage: pageType === "HomePage", // HomePage
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
    };
};

const mapDispatchToProps = {
    setInitialValues,
    setNavDrawerIsOpen,
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledWrapperX = getStyledWrapper("div") as any;

const Logo: React.FunctionComponent<Props> = ({
    fields,
    extendedStyles,
    isSticky,
    setNavDrawerIsOpen,
    setInitialValues,
    navDrawerIsOpen,
    homePageLink,
    isHomePage,
    toggleHeaderColor,
    externalCustomCSS,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(logoStyles, extendedStyles));
    const isEager = useEagerLogoLoading();

    const openDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setInitialValues({});
    };

    const customCssWrapper = {
        css: css`
            ${externalCustomCSS || fields.customCSS}
        `,
    };

    if (fields.isHeaderLogo) {
        return (
            <StyledWrapperX {...styles.wrapper} sticky={isSticky}>
                <Hidden
                    above="md"
                    css={css`
                        margin-right: 24px;
                        @media print {
                            display: none;
                        }
                    `}
                >
                    {navDrawerIsOpen ? (
                        <Clickable
                            onClick={closeDrawer}
                            {...logoStyles.menuTriggerButton}
                            data-test-selector="expandMobileMenu"
                        >
                            <Icon
                                size={25}
                                src="IconClear"
                                color="primary"
                                {...styles.menuIcon}
                                key="close-menu-icon"
                            />
                            <VisuallyHidden>{translate("close menu")}</VisuallyHidden>
                        </Clickable>
                    ) : (
                        <Clickable
                            onClick={openDrawer}
                            {...logoStyles.menuTriggerButton}
                            data-test-selector="expandMobileMenu"
                        >
                            <Icon size={25} src="Menu" color="secondary" {...styles.menuIcon} key="open-menu-icon" />
                            <VisuallyHidden>{translate("open menu")}</VisuallyHidden>
                        </Clickable>
                    )}
                </Hidden>

                <Link href={homePageLink} onClick={closeDrawer} {...styles.linkStyle} className="home-link">
                    <Img
                        src={isSticky || !isHomePage || toggleHeaderColor ? fields.logoImageSticky : fields.logoImage}
                        altText={translate("home")}
                        loading={isEager ? "eager" : "lazy"}
                    />
                </Link>
            </StyledWrapperX>
        );
    }

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.linkWrapper} className="wrapper">
                <Link href={homePageLink} {...styles.logoLinkStyle} className="home-page-link">
                    <Img
                        src={fields.logoImage}
                        altText={translate("img")}
                        loading={isEager ? "eager" : "lazy"}
                        className="img"
                    />
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}
.home-page-link {
}
.img {
}
`;
const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Logo),
    definition: {
        group: "Basic",
        icon: "diamond",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.isHeaderLogo,
                displayName: "Header Specific Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.logoImageSticky,
                displayName: "Sticky Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.isHeaderLogo === true,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
